import React, { useContext, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { RootStateOrAny, useSelector } from "react-redux";
import { performancePages } from "../../../../constants";
import { ThemeContext } from "../../../../context";

import { getBrandOaOAverage, getBrandOaOTrend } from "../../../../api/brandPerformanceApi";
import { withRouter, RouteComponentProps } from "react-router-dom";
import BrandPerformanceTrendTile from "../BrandPerformanceTrendTile";
import BrandPerformanceTableTile from "../BrandPerformanceTableTile";
import { isNull, isNullOrUndefined } from "../../../../utils/utilityFunctions";

interface Props extends RouteComponentProps {
  onHandleRadioClick: any;
  nissanCompetitor: string;
  activePage: performancePages;
  diff: number;
  granularity: string;
}

const TableTooltipParagraphs = () => (
  <>
    <p>
      <span style={{ fontWeight: "bold" }}>Overall opinion </span>
      shows the percentage of &#34;Excellent&#34; given as raiting as result of the following question: &#34;What is your overall oponion of
      each make/model (based on what you know or have heard)?&#34;
    </p>
    <p>
      <u>Source</u>: GfK Imagery and Awareness
    </p>
    <br />
  </>
);

const TrendTooltipParagraph = () => (
  <>
    <p>
      <span style={{ fontWeight: "bold" }}>Overall opinion average </span>
      considers the average score given to a make/model in the selected period. Please note that OaO average is calculated by the sum of all
      scores divided by the number of months selected.
    </p>
    <p>
      <u>Source</u>: GfK Imagery and Awareness
    </p>
    <br />
  </>
);

const BrandOaO = withRouter((props: Props) => {
  const { theme } = useContext(ThemeContext);
  const { onHandleRadioClick, nissanCompetitor, diff, history, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);

  const model = params.model;

  // fetch the trend and average data
  const {
    data: chartData,
    isFetching: loadingTrend, // call data using react-query
    refetch: refetchBrandOaOTrend,
  } = useQuery(["brandBrandOaoTrend", granularity, history.location.search], () => getBrandOaOTrend({ ...params, nissan_competitor: nissanCompetitor }), {
    enabled: false,
  });

  const {
    data: tableData,
    isFetching: loadingTable,
    refetch: refetchBrandOaOAverage,
  } = useQuery(["brandOaOAverage", granularity, history.location.search], () => getBrandOaOAverage({ ...params, nissan_competitor: nissanCompetitor }), {
    enabled: false,
  });

  // when the url changes, refetch the data
  useEffect(() => {
    refetchBrandOaOAverage();
    refetchBrandOaOTrend();
  }, [history.location.search]);

  const columns = [
    {
      id: "model",
      Header: `${model == "Brand" ? "Brand" : "Model"} `,
      accessor: "model",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "alphanumericIgnoreCase",
      Cell: (props1: { value: string }) => {
        const { value } = props1;

        return value;
      },
    },
    {
      Header: "OaO",
      accessor: `percentage_average_score`,
      id: "percentage_average_score",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props: { cell: { row: { original: any } } }) => {
        const {
          cell: {
            row: { original },
          },
        } = props;
        const value = original?.["percentage_average_score"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
    {
      Header: "YoY",
      accessor: `yoy`,
      id: "yoy",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props: { cell: { row: { original: any } } }) => {
        const {
          cell: {
            row: { original },
          },
        } = props;
        const value = original?.["yoy"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
  ];

  const dateRange = tableData?.date_range ?? [];
  const dateTitle = useMemo(() => {
    if (dateRange && dateRange.length > 1) {
      return `(${dateRange[0]} - ${dateRange.slice(-1)})`;
    }

    return "";
  }, [dateRange]);

  return (
    <div className="brand-market-main-content">
      <BrandPerformanceTrendTile
        title="Brand OaO Trend"
        chartId="marketShareTrend"
        chartData={chartData}
        loadingTrend={loadingTrend}
        diff={diff}
        TrendTooltipParagraph={TrendTooltipParagraph}
        nissanCompetitor={nissanCompetitor}
        onHandleRadioClick={onHandleRadioClick}
        chartGranularity={granularity}
        isPercentage={true}
      />
      <BrandPerformanceTableTile
        title="OaO Average"
        tableId="brand_market_share_table"
        tableData={tableData}
        columns={columns}
        loadingTable={loadingTable}
        initialSortCol={{ id: "market_share", desc: true }}
        TableTooltipParagraphs={TableTooltipParagraphs}
      />
    </div>
  );
});

export default BrandOaO;
